import { memo, useMemo } from 'react';

import { type AssetFileKind } from '@air/api/types';

import { FilterRowWithCheckbox } from '~/components/Filters/FilterRowWithCheckbox';
import { type ItemTypeFilter, TYPE_FILTERS } from '~/components/Filters/types';

export const defaultTypes: ItemTypeFilter[] = ['boards', 'files', 'assets'];

export type TypeFilterProps = {
  availableTypes?: ItemTypeFilter[];
  selectedTypes: ItemTypeFilter[];
  selectedAssetFileKinds: AssetFileKind[];
  onAssetFileKindSelectedChange: (params: {
    assetFileKind: AssetFileKind;
    isSelected: boolean;
    type: ItemTypeFilter;
  }) => void;
  onTypeSelectedChange: (params: { type: ItemTypeFilter; isSelected: boolean }) => void;
};

export const TypeFilter = memo(
  ({
    availableTypes = defaultTypes,
    selectedTypes,
    selectedAssetFileKinds,
    onTypeSelectedChange,
    onAssetFileKindSelectedChange,
  }: TypeFilterProps) => {
    const filteredTypes = useMemo(() => {
      return Object.values(TYPE_FILTERS).filter((typeFilter) => availableTypes.includes(typeFilter.id));
    }, [availableTypes]);

    return (
      <div>
        {filteredTypes.map((typeFilter) => {
          const isSelected = selectedTypes.includes(typeFilter.id);
          const isPartialSelected =
            typeFilter.subFilters &&
            Object.values(typeFilter.subFilters).some((subFilter) => selectedAssetFileKinds.includes(subFilter.id));

          return (
            <div key={typeFilter.id}>
              <FilterRowWithCheckbox
                icon={typeFilter.icon}
                label={typeFilter.label}
                isSelected={isPartialSelected || isSelected}
                isPartialSelected={isPartialSelected}
                onChange={(isSelected) => onTypeSelectedChange({ type: typeFilter.id, isSelected })}
              />
              {typeFilter.subFilters && (
                <div className="pl-6">
                  {Object.values(typeFilter.subFilters).map((subFilter) => {
                    return (
                      <FilterRowWithCheckbox
                        key={subFilter.id}
                        icon={subFilter.icon}
                        label={subFilter.label}
                        isSelected={selectedAssetFileKinds.includes(subFilter.id) || isSelected}
                        onChange={(isSelected) =>
                          onAssetFileKindSelectedChange({
                            assetFileKind: subFilter.id,
                            isSelected,
                            type: typeFilter.id,
                          })
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  },
);

TypeFilter.displayName = 'TypeFilter';
